import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { UserAvatar } from '@/components/UserAvatar';
import { useConfirm } from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { showLimitError } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Mail,
  Building2,
  Globe,
  Award,
  Flame,
  Zap,
  Target,
  TrendingUp,
  MessageSquare,
  ThumbsUp,
  Bookmark,
  Send,
  BarChart3,
  Clock,
  RotateCcw,
  Edit2,
  Save,
  X,
  Copy,
  Check,
  Loader2,
  HelpCircle,
  Star,
  Users,
  BookOpen,
  Crown,
  Sparkles,
  Shield,
  Eye,
  Activity,
  CheckCircle,
  UserPlus,
  UserMinus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ANALYTICS_RANGE_OPTIONS = [
  { value: 'daily', label: 'Daily', days: 1 },
  { value: 'weekly', label: 'Weekly', days: 7 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'all', label: 'All Time', days: null },
];
const COUNTRY_OPTIONS = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Ireland',
  'Poland',
  'Portugal',
  'India',
  'Japan',
  'South Korea',
  'China',
  'Singapore',
  'Hong Kong',
  'Brazil',
  'Mexico',
  'Argentina',
  'South Africa',
  'United Arab Emirates',
  'Saudi Arabia',
  'Israel',
  'New Zealand',
  'Other',
];

// Content Team (Eva Insights) roles, mirrors TeamManagementPage / users_schema CONTENT_TEAM_ROLES.
const CONTENT_TEAM_ROLES = ['admin', 'approver', 'analyst', 'viewer'];
const ACCESS_ROLE_CONFIG = {
  admin: { label: 'Admin', icon: Shield, description: 'Full access — manage team, configure exports, approve KB' },
  approver: { label: 'Approver', icon: CheckCircle, description: 'View insights, approve/reject and ingest KB updates' },
  analyst: { label: 'Analyst', icon: Activity, description: 'View insights, export and schedule reports' },
  viewer: { label: 'Viewer', icon: Eye, description: 'Read-only access to dashboards' },
};
// Ascending-privilege order for the picker.
const ACCESS_ROLE_ORDER = ['viewer', 'analyst', 'approver', 'admin'];

export default function EmployeeDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser, hasRole } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(false);
  const [resettingUsage, setResettingUsage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingNudge, setSendingNudge] = useState(false);
  const [loadingNudges, setLoadingNudges] = useState(false);
  const [showNudgePicker, setShowNudgePicker] = useState(false);
  const [nudgeOptions, setNudgeOptions] = useState([]);
  const [selectedNudgeIndex, setSelectedNudgeIndex] = useState(0);
  const [analyticsRange, setAnalyticsRange] = useState('monthly');
  const [emailCopied, setEmailCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    department: '',
    country: '',
  });
  const [userRoles, setUserRoles] = useState(null); // null until loaded
  const [accessRole, setAccessRole] = useState('viewer');
  const [savingAccess, setSavingAccess] = useState(false);
  const [ConfirmEl, confirm] = useConfirm();

  const canEdit = currentUser?.platform_role === 'admin' || currentUser?.platform_role === 'superadmin' || currentUser?.is_superadmin;
  const canSendNudge = hasRole(['admin', 'analyst', 'superadmin']) || currentUser?.is_superadmin;

  const contentTeamRole = (userRoles || []).find((role) => CONTENT_TEAM_ROLES.includes(role)) || null;
  const isContentTeam = Boolean(contentTeamRole);
  const isSelf = Boolean(
    employee?.email && currentUser?.email && employee.email.toLowerCase() === currentUser.email.toLowerCase()
  );

  const fetchUserRoles = useCallback(async () => {
    if (!token || !userId) return;
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUserRoles((data.roles || []).map((entry) => entry.role));
      }
    } catch (_error) {
      // Non-fatal: the access card simply stays in its loading state.
    }
  }, [token, userId]);

  // Load roles only for admins, who are the only ones who can act on them.
  useEffect(() => {
    if (canEdit) fetchUserRoles();
  }, [canEdit, fetchUserRoles]);

  // Default the picker to the user's current role once roles are known.
  useEffect(() => {
    if (userRoles) {
      const current = userRoles.find((role) => CONTENT_TEAM_ROLES.includes(role));
      setAccessRole(current || 'viewer');
    }
  }, [userRoles]);

  const handleSaveAccessRole = async () => {
    setSavingAccess(true);
    try {
      const response = await fetch(`${API_URL}/api/team/members/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: accessRole }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`Role updated to ${ACCESS_ROLE_CONFIG[accessRole]?.label || accessRole}`);
        await fetchUserRoles();
      } else {
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (_error) {
      toast.error('Failed to update role');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleAddToContentTeam = async () => {
    setSavingAccess(true);
    try {
      const { data } = await api.post('/team/invite', { email: employee.email, role: accessRole });
      toast.success(data?.message || 'Added to Content Team');
      await fetchUserRoles();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      showLimitError(detail || error?.message, navigate, 'Failed to add to Content Team');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleRemoveFromContentTeam = async () => {
    const ok = await confirm({
      title: 'Remove from Content Team',
      description: `Remove ${employee.email} from the Content Team? They keep their employee account but lose Eva Insights content access.`,
      confirmLabel: 'Remove',
      variant: 'delete',
    });
    if (!ok) return;
    setSavingAccess(true);
    try {
      const response = await fetch(`${API_URL}/api/team/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success('Removed from Content Team');
        await fetchUserRoles();
      } else {
        toast.error(data.detail || 'Failed to remove from Content Team');
      }
    } catch (_error) {
      toast.error('Failed to remove from Content Team');
    } finally {
      setSavingAccess(false);
    }
  };
  const selectedAnalyticsRange = ANALYTICS_RANGE_OPTIONS.find((option) => option.value === analyticsRange) || ANALYTICS_RANGE_OPTIONS[2];
  const selectedAnalyticsDays = selectedAnalyticsRange.days;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(employee.email);
      setEmailCopied(true);
      toast.success('Email copied');
      window.setTimeout(() => setEmailCopied(false), 1800);
    } catch (_error) {
      toast.error('Unable to copy email');
    }
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const employeeQuery = selectedAnalyticsDays ? `?days=${selectedAnalyticsDays}` : '';
        const response = await fetch(`${API_URL}/api/employees/${userId}${employeeQuery}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setEmployee(data);
          setEditForm({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            job_title: data.job_title || '',
            department: data.department || '',
            country: data.country || '',
          });
          setTokenUsageLoading(true);
          const usageDays = selectedAnalyticsDays || 3650;
          fetch(`${API_URL}/api/usage/by-user?days=${usageDays}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((usageResponse) => (usageResponse.ok ? usageResponse.json() : null))
            .then((usageData) => {
              const rows = usageData?.by_user || [];
              const match = rows.find((row) => String(row.user_email || '').toLowerCase() === String(data.email || '').toLowerCase());
              setTokenUsage(match || null);
            })
            .catch((_usageError) => {
              setTokenUsage(null);
            })
            .finally(() => {
              setTokenUsageLoading(false);
            });
        }
      } catch (_error) {
        console.error('Failed to fetch employee:', _error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [userId, token, selectedAnalyticsDays]);

  const refetchTokenUsage = async () => {
    if (!employee?.email) return;
    const usageDays = selectedAnalyticsDays || 3650;
    try {
      const usageResponse = await fetch(`${API_URL}/api/usage/by-user?days=${usageDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const usageData = usageResponse.ok ? await usageResponse.json() : null;
      const rows = usageData?.by_user || [];
      const match = rows.find((row) => String(row.user_email || '').toLowerCase() === String(employee.email || '').toLowerCase());
      setTokenUsage(match || null);
    } catch (_usageError) {
      // keep the current view; the reset itself already succeeded
    }
  };

  const handleResetUsage = async () => {
    if (!employee?.email) return;
    const targetName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email;
    const ok = await confirm({
      title: 'Reset usage limit',
      description: (
        `Reset ${targetName}'s personal AI usage limit so they can keep working now? `
        + 'Their usage still counts toward your organization’s monthly credit '
        + 'allocation, so this does not add credits — and if the whole '
        + 'organization’s monthly limit is reached, they stay paused until it resets.'
      ),
      confirmLabel: 'Reset limit',
    });
    if (!ok) return;
    setResettingUsage(true);
    try {
      await api.post('/usage/users/reset', { user_email: employee.email });
      toast.success('Usage limit reset');
      await refetchTokenUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset usage limit');
    } finally {
      setResettingUsage(false);
    }
  };

  const handleOpenNudgePicker = async () => {
    setLoadingNudges(true);
    try {
      const response = await fetch(`${API_URL}/api/employees/${userId}/nudge-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load nudge options');
      }
      const data = await response.json();
      const options = data.nudges || [];
      if (options.length === 0) {
        toast.error('No dynamic nudges are available for this employee');
        return;
      }
      const selected = data.selected
        ? Math.max(0, options.findIndex((nudge) => nudge.text === data.selected.text && nudge.type === data.selected.type))
        : 0;
      setNudgeOptions(options);
      setSelectedNudgeIndex(selected >= 0 ? selected : 0);
      setShowNudgePicker(true);
    } catch (_error) {
      toast.error('Failed to load nudge options');
    } finally {
      setLoadingNudges(false);
    }
  };

  const handleSendNudge = async () => {
    const selectedNudge = nudgeOptions[selectedNudgeIndex];
    if (!selectedNudge) {
      toast.error('Choose a nudge before sending');
      return;
    }

    setSendingNudge(true);
    try {
      const response = await fetch(`${API_URL}/api/employees/${userId}/nudge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nudge: selectedNudge }),
      });
      if (response.ok) {
        const data = await response.json();
        setShowNudgePicker(false);
        toast.success('✅ Nudge Sent!', {
          description: `${data.nudge?.icon || selectedNudge.icon} ${data.nudge?.text || selectedNudge.text}`,
        });
      } else {
        throw new Error('Failed to send');
      }
    } catch (_error) {
      toast.error('Failed to send nudge');
    } finally {
      setSendingNudge(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setEmployee(prev => ({
          ...prev,
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          job_title: updatedUser.job_title,
          department: updatedUser.department,
          country: updatedUser.country,
        }));
        setIsEditing(false);
        toast.success('Employee details updated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save changes');
      }
    } catch (_error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      job_title: employee.job_title || '',
      department: employee.department || '',
      country: employee.country || '',
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="6xl"
        kpis={3}
        sections={2}
        sectionClassName="h-72"
        data-testid="employee-detail-loading"
      />
    );
  }

  if (!employee) {
    return (
      <PageShell maxWidth="6xl" contentClassName="text-center">
        <p className="text-gray-500">Employee not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-primary hover:text-primary/80">
          Go Back
        </button>
      </PageShell>
    );
  }

  const { gamification } = employee;
  const latestActivityDate = getLatestActivityDate(employee.engagement_metrics?.last_activity_date, tokenUsage?.last_active);
  const name = isEditing 
    ? `${editForm.first_name || ''} ${editForm.last_name || ''}`.trim() || employee.email.split('@')[0]
    : `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email.split('@')[0];

  return (
    <PageShell maxWidth="6xl" data-testid="employee-detail-page">
      {ConfirmEl}
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 text-gray-500" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Employee Details</h1>
      </div>

      {/* Profile Header Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
            <UserAvatar user={{ ...employee, name }} size="xl" className="shadow-lg" />
            
            {isEditing ? (
              <div className="w-full space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First Name"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-lg font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    data-testid="edit-first-name"
                  />
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last Name"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-lg font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    data-testid="edit-last-name"
                  />
                </div>
                <input
                  type="text"
                  value={editForm.job_title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, job_title: e.target.value }))}
                  placeholder="Job Title"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 w-full"
                  data-testid="edit-job-title"
                />
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex min-w-0 items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <span className="break-all">{employee.email}</span>
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={editForm.department}
                      onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))}
                      placeholder="Department"
                      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      data-testid="edit-department"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <select
                      value={editForm.country}
                      onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      data-testid="edit-country"
                    >
                      <option value="">Select country...</option>
                      {COUNTRY_OPTIONS.map((country) => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{name}</h2>
                <p className="text-gray-500 dark:text-gray-400">{employee.job_title || 'Employee'}</p>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex min-w-0 items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <span className="break-all">{employee.email}</span>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className="ml-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      aria-label="Copy email address"
                      title="Copy email address"
                      data-testid="copy-email-button"
                    >
                      {emailCopied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </span>
                  {employee.department && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {employee.department}
                    </span>
                  )}
                  {employee.country && (
                    <span className="flex items-center gap-1">
                      <Globe className="w-4 h-4" />
                      {employee.country}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-2 sm:flex-row lg:justify-end">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  data-testid="cancel-edit-button"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  data-testid="save-edit-button"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="edit-button"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
                {canSendNudge && (
                  <button
                    onClick={handleOpenNudgePicker}
                    disabled={loadingNudges || sendingNudge}
                    className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                    data-testid="send-nudge-button"
                  >
                    {loadingNudges ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {loadingNudges ? 'Loading...' : 'Send Nudge'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Eva Insights Access — change roles & content-team membership after creation (EIP-190) */}
      {canEdit && (
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 mb-6"
          data-testid="employee-access-card"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Eva Insights Access</h3>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage this user&apos;s Content Team role and permissions.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="access-current-status">
            {isContentTeam ? (
              <>
                <span className="text-sm text-gray-600 dark:text-gray-300">Content Team member ·</span>
                {(() => {
                  const RoleIcon = ACCESS_ROLE_CONFIG[contentTeamRole]?.icon || Eye;
                  return (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                      <RoleIcon className="w-3 h-3" />
                      {ACCESS_ROLE_CONFIG[contentTeamRole]?.label || contentTeamRole}
                    </span>
                  );
                })()}
              </>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Not on the Content Team — no Eva Insights content access.
              </span>
            )}
          </div>

          {isSelf ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="access-self-note">
              You can&apos;t change your own access. Ask another admin to update your role.
            </p>
          ) : userRoles === null ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading access…
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="max-w-xs">
                <label
                  htmlFor="access-role-select"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Content Team role
                </label>
                <select
                  id="access-role-select"
                  value={accessRole}
                  onChange={(e) => setAccessRole(e.target.value)}
                  disabled={savingAccess}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  data-testid="access-role-select"
                >
                  {ACCESS_ROLE_ORDER.map((role) => (
                    <option key={role} value={role}>
                      {ACCESS_ROLE_CONFIG[role].label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {ACCESS_ROLE_CONFIG[accessRole]?.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {isContentTeam ? (
                  <>
                    <button
                      onClick={handleSaveAccessRole}
                      disabled={savingAccess || accessRole === contentTeamRole}
                      className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                      data-testid="save-access-role-button"
                    >
                      {savingAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Update role
                    </button>
                    <button
                      onClick={handleRemoveFromContentTeam}
                      disabled={savingAccess}
                      className="flex items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
                      data-testid="remove-content-team-button"
                    >
                      <UserMinus className="w-4 h-4" />
                      Remove from Content Team
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAddToContentTeam}
                    disabled={savingAccess}
                    className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                    data-testid="add-content-team-button"
                  >
                    {savingAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Add to Content Team
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showNudgePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Send Dynamic Nudge</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Choose the exact nudge {name} will receive in Eva People.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowNudgePicker(false)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close nudge picker"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              {nudgeOptions.map((nudge, index) => (
                <button
                  key={`${nudge.type}-${nudge.text}`}
                  type="button"
                  onClick={() => setSelectedNudgeIndex(index)}
                  className={`w-full rounded-xl border p-4 text-left transition-colors ${
                    selectedNudgeIndex === index
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/60'
                  }`}
                  data-testid={`nudge-option-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{nudge.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {nudge.type}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                          {nudge.source?.replaceAll('_', ' ') || 'dynamic'}
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{nudge.text}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-5 dark:border-gray-700 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowNudgePicker(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendNudge}
                disabled={sendingNudge}
                className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                data-testid="confirm-send-nudge-button"
              >
                {sendingNudge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sendingNudge ? 'Sending...' : 'Send Selected Nudge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Level */}
        <div className="rounded-xl border border-primary/20 bg-primary p-5 text-primary-foreground">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-foreground/80 text-sm">Level</p>
              <p className="text-4xl font-bold">{gamification?.level || 1}</p>
              <p className="text-primary-foreground/80 text-sm mt-1">
                {gamification?.points_to_next_level || 0} pts to next level
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Award className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* Points */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Points</p>
              <p className="text-4xl font-bold text-gray-900 dark:text-white">{gamification?.points || 0}</p>
              <p className="text-gray-400 text-sm mt-1">
                Rank #{gamification?.rank || '—'}
              </p>
            </div>
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <Zap className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Streak */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Current Streak</p>
              <p className="text-4xl font-bold text-gray-900 dark:text-white">{gamification?.current_streak || 0}</p>
              <p className="text-gray-400 text-sm mt-1">
                Best: {gamification?.best_streak || 0} days
              </p>
            </div>
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
              <Flame className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          Badges Earned ({gamification?.badges?.length || 0})
        </h3>
        {gamification?.badges?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {gamification.badges.map((badge, index) => {
              // Badge color schemes - matching Eva People design
              const colorSchemes = [
                { bg: 'bg-gradient-to-br from-purple-500 to-indigo-600', icon: 'text-white' },
                { bg: 'bg-gradient-to-br from-pink-500 to-rose-500', icon: 'text-white' },
                { bg: 'bg-gradient-to-br from-cyan-400 to-blue-500', icon: 'text-white' },
                { bg: 'bg-gradient-to-br from-emerald-400 to-teal-500', icon: 'text-white' },
                { bg: 'bg-gradient-to-br from-amber-400 to-orange-500', icon: 'text-white' },
                { bg: 'bg-gradient-to-br from-violet-500 to-purple-600', icon: 'text-white' },
              ];
              const colors = colorSchemes[index % colorSchemes.length];

              // Map badge types to lucide icons
              const getBadgeIcon = (badgeName) => {
                const name = (badgeName || '').toLowerCase();
                if (name.includes('first') || name.includes('start')) return <Zap className="w-5 h-5" />;
                if (name.includes('streak') || name.includes('fire')) return <Flame className="w-5 h-5" />;
                if (name.includes('question') || name.includes('curious')) return <HelpCircle className="w-5 h-5" />;
                if (name.includes('star') || name.includes('top') || name.includes('rising')) return <Star className="w-5 h-5" />;
                if (name.includes('target') || name.includes('goal')) return <Target className="w-5 h-5" />;
                if (name.includes('trend') || name.includes('grow')) return <TrendingUp className="w-5 h-5" />;
                if (name.includes('award') || name.includes('achieve') || name.includes('expert')) return <Award className="w-5 h-5" />;
                if (name.includes('team') || name.includes('social')) return <Users className="w-5 h-5" />;
                if (name.includes('book') || name.includes('learn') || name.includes('knowledge') || name.includes('seeker')) return <BookOpen className="w-5 h-5" />;
                if (name.includes('crown') || name.includes('king') || name.includes('unstoppable')) return <Crown className="w-5 h-5" />;
                if (name.includes('feedback') || name.includes('helpful')) return <ThumbsUp className="w-5 h-5" />;
                return <Award className="w-5 h-5" />;
              };

              return (
                <div
                  key={index}
                  className="group relative"
                  title={`${badge.name}: ${badge.description}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center shadow-sm hover:scale-110 transition-transform cursor-pointer`}>
                    <span className={colors.icon}>{getBadgeIcon(badge.name)}</span>
                  </div>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-48 text-center z-50">
                    <p className="font-medium">{badge.name}</p>
                    <p className="text-gray-300 mt-1">{badge.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyCardState
            icon={Award}
            title="No badges earned yet"
            description="Badges will show here as this employee builds streaks, asks questions, and shares feedback."
            compact
          />
        )}
      </div>

      {/* Activity Window */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Activity Window</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Applies to engagement, AI usage, badges, and topic categories.</p>
        </div>
        <div className="inline-flex rounded-lg bg-gray-100 p-1 dark:bg-gray-800" aria-label="Activity window">
          {ANALYTICS_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setAnalyticsRange(option.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                analyticsRange === option.value
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Engagement Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Engagement Metrics
          </h3>
          <div className="space-y-4">
            <MetricRow
              icon={<MessageSquare className="w-4 h-4" />}
              label="Questions Asked"
              value={employee.engagement_metrics?.questions_asked || 0}
              color="primary"
            />
            <MetricRow
              icon={<ThumbsUp className="w-4 h-4" />}
              label="Helpful Responses"
              value={`${employee.engagement_metrics?.helpful_rate || 0}%`}
              subtext={`${employee.engagement_metrics?.helpful_responses || 0} helpful`}
              color="green"
            />
            <MetricRow
              icon={<Bookmark className="w-4 h-4" />}
              label="Bookmarks Saved"
              value={employee.engagement_metrics?.bookmarks_saved || 0}
              color="yellow"
            />
            <MetricRow
              icon={<Target className="w-4 h-4" />}
              label="Feedback Given"
              value={employee.engagement_metrics?.feedback_given || 0}
              subtext={`${employee.engagement_metrics?.feedback_rate || 0}% rate`}
              color="blue"
            />
            <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Last Active
                </span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {latestActivityDate
                    ? latestActivityDate.toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
            <Zap className="w-5 h-5 text-primary" />
            AI Usage
          </h3>

          {tokenUsageLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading AI usage...
            </div>
          ) : tokenUsage ? (
            <div className="space-y-4">
              <MetricRow
                icon={<MessageSquare className="w-4 h-4" />}
                label="Requests"
                value={Number(tokenUsage.request_count || 0).toLocaleString()}
                color="primary"
              />
              <MetricRow
                icon={<Zap className="w-4 h-4" />}
                label="Usage"
                value={formatTokens(tokenUsage.total_tokens)}
                color="yellow"
              />
              <MetricRow
                icon={<TrendingUp className="w-4 h-4" />}
                label="Usage Share"
                value={formatPercent(tokenUsage.percent_of_total)}
                subtext="of tenant total"
                color="blue"
              />
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Rate limit</span>
                  <span className="font-medium capitalize text-gray-900 dark:text-white">
                    {tokenUsage.capacity?.is_unlimited
                      ? 'Unlimited'
                      : tokenUsage.capacity?.window || 'Not configured'}
                  </span>
                </div>
                {tokenUsage.capacity?.is_unlimited ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No usage rate limit applies.</p>
                ) : tokenUsage.capacity ? (
                  <>
                    <div className="mb-1 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                      <div
                        className={`h-full rounded-full ${getCapacityFillClass(tokenUsage.capacity)}`}
                        style={{ width: `${Math.min(Number(tokenUsage.capacity.percent_used || 0), 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatPercent(tokenUsage.capacity.percent_used)} used</span>
                      <span>Resets in {tokenUsage.capacity.resets_in_label || 'soon'}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No rate limit is configured for this user.</p>
                )}
                {canEdit && tokenUsage.capacity && !tokenUsage.capacity.is_unlimited && (
                  <button
                    onClick={handleResetUsage}
                    disabled={resettingUsage}
                    className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="reset-usage-button"
                    title="Reset this user's personal AI usage limit"
                  >
                    {resettingUsage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Reset limit
                  </button>
                )}
              </div>

              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Last AI Activity
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {tokenUsage.last_active ? new Date(tokenUsage.last_active).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <MetricRow
                icon={<MessageSquare className="w-4 h-4" />}
                label="Requests"
                value="0"
                color="primary"
              />
              <MetricRow
                icon={<Zap className="w-4 h-4" />}
                label="Usage"
                value="0"
                color="yellow"
              />
              <MetricRow
                icon={<TrendingUp className="w-4 h-4" />}
                label="Usage Share"
                value="0.0%"
                subtext="of tenant total"
                color="blue"
              />
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Last AI Activity
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">Never</span>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Top Topics */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Topic Categories
        </h3>
        {employee.top_topics?.length > 0 ? (
          <div className="space-y-3">
            {employee.top_topics.map((topic, index) => (
              <div
                key={index}
                className="flex flex-col gap-1 rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="text-gray-700 dark:text-gray-300">{topic.topic}</span>
                <span className="text-sm text-gray-500">{topic.count} questions</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyCardState
            icon={Sparkles}
            title="No topic activity yet"
            description="Topic categories will populate after Eva has enough conversation history for this employee."
            wide
          />
        )}
      </div>
    </PageShell>
  );
}

function formatTokens(value) {
  const num = Number(value || 0);
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
  return num.toLocaleString();
}

function formatPercent(value) {
  const num = Number(value || 0);
  return `${num.toFixed(1)}%`;
}

function getCapacityFillClass(capacity) {
  if (capacity?.limit_exceeded) return 'bg-red-500';
  if (Number(capacity?.percent_used || 0) >= 80) return 'bg-yellow-500';
  return 'bg-primary';
}

function EmptyCardState({ icon: Icon, title, description, compact = false, wide = false }) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-6 text-center dark:border-gray-700 dark:bg-gray-900/25 ${
        compact ? 'min-h-40 py-8' : wide ? 'min-h-44 py-10' : 'min-h-48 py-10'
      }`}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
      <p className="mt-2 max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
}

function getLatestActivityDate(...values) {
  const dates = values
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((date) => date.getTime())));
}

function MetricRow({ icon, label, value, subtext, color = 'gray' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600',
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
      </div>
      <div className="text-right">
        <span className="font-semibold text-gray-900 dark:text-white">{value}</span>
        {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
      </div>
    </div>
  );
}
